import Clipboard from '@react-native-clipboard/clipboard';
import { useMemo } from 'react';
import { CopyIcon, FruitfulCustomIcon, Icon, XStack, YStack } from 'smoothie';

import {
    getFruitfulProductNameFromAccountType,
    maskValue,
} from '../../../common/utils/bankingUtils';
import type { BankAccountDisplayDetail } from '../../types/BankAccount';
import {
    DetailsTile,
    DetailsTileLabel,
    DetailsTileValue,
    useCopiedToast,
    useToggleSensitiveDataVisibility,
} from './DetailsTile';

export const DetailsTileForAccount = ({
    account,
}: {
    account?: BankAccountDisplayDetail | null;
}) => {
    const { showCopiedToast } = useCopiedToast();
    const { isSensitiveDataVisible, toggleSensitiveDataVisibility } =
        useToggleSensitiveDataVisibility();

    const accountProductName =
        account == null
            ? 'account'
            : getFruitfulProductNameFromAccountType(account.accountType);
    const accountNumber = account?.accountNumber ?? '****';
    const routingNumber = account?.routingNumber ?? '****';
    const accountNumberMasked = useMemo(
        () =>
            maskValue({
                value: accountNumber,
                maskedLength: accountNumber.length - 4,
            }),
        [accountNumber],
    );

    if (account == null) {
        return null;
    }

    return (
        <DetailsTile
            account={account}
            icon={
                <FruitfulCustomIcon
                    icon={account.icon}
                    color={account.color}
                    size="sm"
                />
            }
            name={account.nickname ?? 'Fruitful account'}
            description={`${accountProductName} account`}
            isSensitiveDataVisible={isSensitiveDataVisible}
            toggleSensitiveDataVisibility={toggleSensitiveDataVisibility}
        >
            <AccountDetail
                label="Routing number"
                value={routingNumber}
                valueMasked={
                    routingNumber
                } /* we intentionally do not mask the routing number */
                isSensitiveDataVisible={isSensitiveDataVisible}
                onPressCopy={() => {
                    Clipboard.setString(routingNumber);
                    showCopiedToast();
                }}
            />
            <AccountDetail
                label="Account number"
                value={accountNumber}
                valueMasked={accountNumberMasked}
                isSensitiveDataVisible={isSensitiveDataVisible}
                onPressCopy={() => {
                    Clipboard.setString(accountNumber);
                    showCopiedToast();
                }}
            />
        </DetailsTile>
    );
};

const AccountDetail = ({
    label,
    value,
    valueMasked,
    isSensitiveDataVisible,
    onPressCopy,
}: {
    label: string;
    value: string;
    valueMasked: string;
    isSensitiveDataVisible: boolean;
    onPressCopy: (value: string) => void;
}) => {
    return (
        <YStack gap={2}>
            <DetailsTileLabel text={label} />
            <XStack
                gap={4}
                alignItems="center"
                onPress={() => {
                    onPressCopy(value);
                }}
                cursor="pointer"
            >
                <DetailsTileValue
                    value={isSensitiveDataVisible ? value : valueMasked}
                />
                <Icon icon={CopyIcon} color="$black" />
            </XStack>
        </YStack>
    );
};
